(() => {
  const t = (key, fallback = '', replacements = {}) => window.MDT ? window.MDT(key, fallback, replacements) : fallback;
  const debounce = (fn, delay = 250) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), delay);
    };
  };

  class MDQuickOrder {
    constructor(root) {
      this.root = root;
      this.rootUrl = (root.dataset.rootUrl || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      this.predictiveUrl = root.dataset.predictiveUrl || `${this.rootUrl}search/suggest`;
      this.cartUrl = root.dataset.cartUrl || `${this.rootUrl}cart`;
      this.currency = root.dataset.currency || 'EUR';
      this.locale = root.dataset.locale || 'nl-NL';
      this.searchSection = root.dataset.sectionSearch || 'md-quick-order-search';
      this.storageKey = 'md_energeez_quick_order';
      this.storageVersion = 2;
      this.items = new Map();
      this.searchController = null;

      this.searchInput = root.querySelector('[data-md-qo-search]');
      this.searchResults = root.querySelector('[data-md-qo-search-results]');
      this.searchLoader = root.querySelector('[data-md-qo-search-loader]');
      this.itemsContainer = root.querySelector('[data-md-qo-items]');
      this.emptyState = root.querySelector('[data-md-qo-empty]');
      this.clearButton = root.querySelector('[data-md-qo-clear]');
      this.pasteToggle = root.querySelector('[data-md-qo-paste-toggle]');
      this.pastePanel = root.querySelector('[data-md-qo-paste-panel]');
      this.pasteInput = root.querySelector('[data-md-qo-paste-input]');
      this.pasteSubmit = root.querySelector('[data-md-qo-paste-submit]');
      this.pasteCancel = root.querySelector('[data-md-qo-paste-cancel]');
      this.pasteStatus = root.querySelector('[data-md-qo-paste-status]');
      this.rowCount = root.querySelector('[data-md-qo-row-count]');
      this.unitCount = root.querySelector('[data-md-qo-unit-count]');
      this.subtotal = root.querySelector('[data-md-qo-subtotal]');
      this.addAllButton = root.querySelector('[data-md-qo-add-all]');
      this.addAllLabel = root.querySelector('[data-md-qo-add-all-label]');
      this.cartLink = root.querySelector('[data-md-qo-cart-link]');
      this.status = root.querySelector('[data-md-qo-status]');

      if (!this.searchInput || !this.itemsContainer || !this.addAllButton) return;
      this.bindEvents();
      this.restoreItems();
      this.renderItems(false);
      this.refreshStoredItems();
    }

    bindEvents() {
      this.searchInput.addEventListener('input', debounce(() => this.onSearch(), 240));
      this.searchInput.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeSearch();
      });
      this.searchInput.addEventListener('focus', () => {
        if (this.searchResults && this.searchResults.innerHTML.trim()) this.openSearch();
      });

      document.addEventListener('click', (event) => {
        if (!this.root.contains(event.target)) return;
        const result = event.target.closest('[data-md-qo-result]');
        if (result) {
          this.addResult(result);
          return;
        }
        const remove = event.target.closest('[data-md-qo-remove]');
        if (remove) {
          this.removeItem(remove.dataset.variantId);
          return;
        }
        const qtyMinus = event.target.closest('[data-md-qo-qty-minus]');
        if (qtyMinus) {
          this.stepQuantity(qtyMinus.dataset.variantId, -1);
          return;
        }
        const qtyPlus = event.target.closest('[data-md-qo-qty-plus]');
        if (qtyPlus) {
          this.stepQuantity(qtyPlus.dataset.variantId, 1);
        }
      });

      document.addEventListener('click', (event) => {
        if (!this.root.contains(event.target)) this.closeSearch();
      });

      this.itemsContainer.addEventListener('change', (event) => {
        const input = event.target.closest('[data-md-qo-qty-input]');
        if (!input) return;
        this.setQuantity(input.dataset.variantId, Number(input.value));
      });

      if (this.pasteToggle) this.pasteToggle.addEventListener('click', () => this.togglePastePanel());
      if (this.pasteCancel) this.pasteCancel.addEventListener('click', () => this.togglePastePanel(false));
      if (this.pasteSubmit) this.pasteSubmit.addEventListener('click', () => this.processPaste());
      if (this.clearButton) this.clearButton.addEventListener('click', () => this.clearItems());
      this.addAllButton.addEventListener('click', () => this.addAllToCart());

    }

    restoreItems() {
      let stored;
      try {
        stored = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null');
      } catch (error) {
        stored = null;
      }

      this.items.clear();
      if (!stored || stored.version !== this.storageVersion || !Array.isArray(stored.items)) return;

      stored.items.forEach((rawItem) => {
        if (!rawItem || !rawItem.variantId) return;

        const item = {
          variantId: String(rawItem.variantId),
          productId: String(rawItem.productId || ''),
          title: String(rawItem.title || ''),
          variantTitle: String(rawItem.variantTitle || ''),
          vendor: String(rawItem.vendor || ''),
          sku: String(rawItem.sku || ''),
          price: Number(rawItem.price || 0),
          priceFormatted: String(rawItem.priceFormatted || ''),
          available: rawItem.available !== false,
          min: this.toPositiveInt(rawItem.min, 1),
          increment: this.toPositiveInt(rawItem.increment, 1),
          max: rawItem.max ? this.toPositiveInt(rawItem.max, null) : null,
          url: String(rawItem.url || '#'),
          image: String(rawItem.image || ''),
          quantity: 1,
        };

        item.quantity = this.normalizeQuantity(item, rawItem.quantity);
        this.items.set(item.variantId, item);
      });
    }

    persistItems() {
      try {
        if (this.items.size === 0) {
          window.localStorage.removeItem(this.storageKey);
          return;
        }

        window.localStorage.setItem(this.storageKey, JSON.stringify({
          version: this.storageVersion,
          updatedAt: Date.now(),
          items: [...this.items.values()],
        }));
      } catch (error) {
        // Opslag mag de snelbestelfunctie nooit blokkeren.
      }
    }

    async refreshStoredItems() {
      const queue = [...this.items.values()];
      if (!queue.length) return;

      let changed = false;
      const workerCount = Math.min(3, queue.length);
      const workers = Array.from({ length: workerCount }, async () => {
        while (queue.length) {
          const storedItem = queue.shift();
          if (!storedItem) continue;
          const query = (storedItem.sku || storedItem.title || '').trim();
          if (!query) continue;

          try {
            const markup = await this.fetchSearchMarkup(query, false, false);
            const doc = new DOMParser().parseFromString(`<div>${markup}</div>`, 'text/html');
            const candidates = [...doc.querySelectorAll('[data-md-qo-result]')];
            const exactVariant = candidates.find((candidate) => String(candidate.dataset.variantId || '') === storedItem.variantId);
            const exactSku = storedItem.sku
              ? candidates.find((candidate) => (candidate.dataset.sku || '').trim().toLowerCase() === storedItem.sku.trim().toLowerCase())
              : null;
            const match = exactVariant || exactSku;
            if (!match) continue;

            const currentItem = this.items.get(storedItem.variantId);
            if (!currentItem) continue;
            const refreshed = this.itemFromResult(match, currentItem.quantity);
            if (!refreshed) continue;

            if (refreshed.variantId !== storedItem.variantId) {
              this.items.delete(storedItem.variantId);
            }
            this.items.set(refreshed.variantId, refreshed);
            changed = true;
          } catch (error) {
            // Bewaar de laatst bekende productdata als actualiseren tijdelijk mislukt.
          }
        }
      });

      await Promise.allSettled(workers);
      if (changed) this.renderItems();
    }

    async onSearch() {
      const query = this.searchInput.value.trim();
      if (query.length < 2) {
        this.closeSearch(true);
        return;
      }

      try {
        const html = await this.fetchSearchMarkup(query, true);
        if (!html) {
          this.closeSearch(true);
          return;
        }
        this.searchResults.innerHTML = html;
        this.openSearch();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.searchResults.innerHTML = `<div class="md-qo-search-fragment__empty">${this.escapeHtml(t('quickOrder.searchUnavailable', 'Search is temporarily unavailable.'))}</div>`;
        this.openSearch();
      } finally {
        this.setSearchLoading(false);
      }
    }

    async fetchSearchMarkup(query, abortPrevious = false, showLoading = true) {
      if (abortPrevious && this.searchController) this.searchController.abort();
      const controller = new AbortController();
      if (abortPrevious) this.searchController = controller;
      if (showLoading) this.setSearchLoading(true);

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': '8',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,vendor,variants.title,variants.sku',
        section_id: this.searchSection,
      });

      const response = await fetch(`${this.predictiveUrl}?${params.toString()}`, {
        signal: controller.signal,
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) throw new Error(`Search failed: ${response.status}`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const section = doc.querySelector(`#shopify-section-${CSS.escape(this.searchSection)}`);
      return section ? section.innerHTML : '';
    }

    setSearchLoading(isLoading) {
      if (!this.searchLoader) return;
      this.searchLoader.hidden = !isLoading;
    }

    openSearch() {
      if (!this.searchResults) return;
      this.searchResults.hidden = false;
      this.searchInput.setAttribute('aria-expanded', 'true');
    }

    closeSearch(clear = false) {
      if (!this.searchResults) return;
      this.searchResults.hidden = true;
      this.searchInput.setAttribute('aria-expanded', 'false');
      if (clear) this.searchResults.innerHTML = '';
    }

    addResult(result, quantityOverride = null) {
      const item = this.itemFromResult(result, quantityOverride);
      if (!item || !item.available) return false;

      const existing = this.items.get(item.variantId);
      if (existing) {
        const requested = quantityOverride == null ? existing.quantity + existing.increment : existing.quantity + quantityOverride;
        existing.quantity = this.normalizeQuantity(existing, requested);
        this.items.set(existing.variantId, existing);
      } else {
        this.items.set(item.variantId, item);
      }

      this.renderItems();
      this.closeSearch(true);
      this.searchInput.value = '';
      this.resetCartSuccess();
      return true;
    }

    itemFromResult(result, quantityOverride) {
      const variantId = String(result.dataset.variantId || '');
      if (!variantId) return null;

      const min = this.toPositiveInt(result.dataset.min, 1);
      const increment = this.toPositiveInt(result.dataset.increment, 1);
      const max = result.dataset.max ? this.toPositiveInt(result.dataset.max, null) : null;
      const baseQty = quantityOverride == null ? min : Number(quantityOverride);

      const item = {
        variantId,
        productId: String(result.dataset.productId || ''),
        title: result.dataset.title || '',
        variantTitle: result.dataset.variantTitle || '',
        vendor: result.dataset.vendor || '',
        sku: result.dataset.sku || '',
        price: Number(result.dataset.price || 0),
        priceFormatted: result.dataset.priceFormatted || '',
        available: result.dataset.available === 'true',
        min,
        increment,
        max,
        url: result.dataset.url || '#',
        image: result.dataset.image || '',
        quantity: min,
      };
      item.quantity = this.normalizeQuantity(item, baseQty);
      return item;
    }

    normalizeQuantity(item, rawValue) {
      let value = Number.isFinite(Number(rawValue)) ? Math.round(Number(rawValue)) : item.min;
      value = Math.max(item.min, value);
      const steps = Math.ceil((value - item.min) / item.increment);
      value = item.min + Math.max(0, steps) * item.increment;
      if (item.max) value = Math.min(item.max, value);
      return value;
    }

    renderItems(persist = true) {
      if (!this.itemsContainer) return;
      const items = [...this.items.values()];

      if (items.length === 0) {
        this.itemsContainer.innerHTML = this.emptyState ? this.emptyState.outerHTML : '';
      } else {
        this.itemsContainer.innerHTML = items.map((item) => this.renderItem(item)).join('');
      }

      if (this.clearButton) this.clearButton.hidden = items.length === 0;
      if (persist) this.persistItems();
      this.updateSummary();
    }

    renderItem(item) {
      const image = item.image
        ? `<img src="${this.escapeAttr(item.image)}" alt="" loading="lazy" width="100" height="100">`
        : '<span aria-hidden="true">—</span>';
      const variant = item.variantTitle ? `<span class="md-quick-order__item-variant">${this.escapeHtml(item.variantTitle)}</span>` : '';
      const maxAttr = item.max ? ` max="${item.max}"` : '';
      const stockClass = item.available ? 'is-available' : 'is-unavailable';
      const stockText = item.available ? t('quickOrder.inStock', 'In stock') : t('quickOrder.outOfStock', 'Out of stock');

      return `
        <div class="md-quick-order__item" data-md-qo-item data-variant-id="${this.escapeAttr(item.variantId)}">
          <div class="md-quick-order__item-product">
            <span class="md-quick-order__item-image">${image}</span>
            <span class="md-quick-order__item-copy">
              ${item.vendor ? `<span class="md-quick-order__item-vendor">${this.escapeHtml(item.vendor)}</span>` : ''}
              <a class="md-quick-order__item-title" href="${this.escapeAttr(item.url)}">${this.escapeHtml(item.title)}</a>
              ${variant}
            </span>
          </div>
          <span class="md-quick-order__item-sku">${this.escapeHtml(item.sku || '')}</span>
          <span class="md-quick-order__item-stock ${stockClass}">${stockText}</span>
          <span class="md-quick-order__item-price">${this.escapeHtml(item.priceFormatted || this.formatMoney(item.price))}</span>
          <div class="md-quick-order__qty">
            <button type="button" aria-label="${this.escapeAttr(t('quickOrder.decreaseQuantity', 'Decrease quantity'))}" data-md-qo-qty-minus data-variant-id="${this.escapeAttr(item.variantId)}">−</button>
            <input type="number" inputmode="numeric" value="${item.quantity}" min="${item.min}" step="${item.increment}"${maxAttr} aria-label="${this.escapeAttr(t('quickOrder.quantityFor', 'Quantity for {title}', { title: item.title }))}" data-md-qo-qty-input data-variant-id="${this.escapeAttr(item.variantId)}">
            <button type="button" aria-label="${this.escapeAttr(t('quickOrder.increaseQuantity', 'Increase quantity'))}" data-md-qo-qty-plus data-variant-id="${this.escapeAttr(item.variantId)}">+</button>
          </div>
          <button class="md-quick-order__remove" type="button" aria-label="${this.escapeAttr(t('quickOrder.removeProduct', 'Remove {title}', { title: item.title }))}" data-md-qo-remove data-variant-id="${this.escapeAttr(item.variantId)}">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>`;
    }

    stepQuantity(variantId, direction) {
      const item = this.items.get(String(variantId));
      if (!item) return;
      const requested = item.quantity + (direction * item.increment);
      if (direction < 0 && requested < item.min) return;
      item.quantity = this.normalizeQuantity(item, requested);
      this.items.set(item.variantId, item);
      this.renderItems();
      this.resetCartSuccess();
    }

    setQuantity(variantId, quantity) {
      const item = this.items.get(String(variantId));
      if (!item) return;
      item.quantity = this.normalizeQuantity(item, quantity);
      this.items.set(item.variantId, item);
      this.renderItems();
      this.resetCartSuccess();
    }

    removeItem(variantId) {
      this.items.delete(String(variantId));
      this.renderItems();
      this.resetCartSuccess();
    }

    clearItems() {
      this.items.clear();
      this.renderItems();
      this.resetCartSuccess();
    }

    updateSummary() {
      const items = [...this.items.values()];
      const rowCount = items.length;
      const unitCount = items.reduce((sum, item) => sum + item.quantity, 0);
      const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

      if (this.rowCount) this.rowCount.textContent = String(rowCount);
      if (this.unitCount) this.unitCount.textContent = String(unitCount);
      if (this.subtotal) this.subtotal.textContent = this.formatMoney(subtotal);
      this.addAllButton.disabled = rowCount === 0 || items.some((item) => !item.available);
    }

    togglePastePanel(force) {
      if (!this.pastePanel || !this.pasteToggle) return;
      const open = typeof force === 'boolean' ? force : this.pastePanel.hidden;
      this.pastePanel.hidden = !open;
      this.pasteToggle.setAttribute('aria-expanded', String(open));
      if (open && this.pasteInput) this.pasteInput.focus();
      if (!open) this.setPasteStatus('');
    }

    parsePasteLines(value) {
      return value
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const parts = line.split(/[\t;,]/).map((part) => part.trim()).filter(Boolean);
          const sku = parts[0] || '';
          const qty = parts[1] ? Number.parseInt(parts[1], 10) : 1;
          return { sku, quantity: Number.isFinite(qty) && qty > 0 ? qty : 1 };
        })
        .filter((entry) => entry.sku);
    }

    async processPaste() {
      const entries = this.parsePasteLines(this.pasteInput ? this.pasteInput.value : '');
      if (!entries.length) {
        this.setPasteStatus(t('quickOrder.pasteMinimum', 'Paste at least one SKU.'), 'error');
        return;
      }

      this.pasteSubmit.disabled = true;
      this.pasteSubmit.textContent = t('quickOrder.processing', 'Processing…');
      this.setPasteStatus(t('quickOrder.processed', '{current} of {total} processed…', { current: 0, total: entries.length }));

      let added = 0;
      const missing = [];

      try {
        for (let index = 0; index < entries.length; index += 1) {
          const entry = entries[index];
          try {
            const markup = await this.fetchSearchMarkup(entry.sku, false);
            const doc = new DOMParser().parseFromString(`<div>${markup}</div>`, 'text/html');
            const candidates = [...doc.querySelectorAll('[data-md-qo-result]')];
            const exact = candidates.find((candidate) => (candidate.dataset.sku || '').trim().toLowerCase() === entry.sku.trim().toLowerCase());

            if (exact && exact.dataset.available === 'true') {
              if (this.addResult(exact, entry.quantity)) added += 1;
            } else {
              missing.push(entry.sku);
            }
          } catch (error) {
            missing.push(entry.sku);
          }

          this.setPasteStatus(t('quickOrder.processed', '{current} of {total} processed…', { current: index + 1, total: entries.length }));
          if (index < entries.length - 1) await this.wait(220);
        }

        if (added > 0 && missing.length === 0) {
          this.setPasteStatus(t('quickOrder.skusAdded', '{count} SKU(s) added to the order list.', { count: added }), 'success');
          if (this.pasteInput) this.pasteInput.value = '';
        } else if (added > 0) {
          this.setPasteStatus(t('quickOrder.addedMissing', '{count} added. Not found or unavailable: {skus}', { count: added, skus: missing.join(', ') }), 'error');
        } else {
          this.setPasteStatus(t('quickOrder.noneAdded', 'No SKUs added. Check: {skus}', { skus: missing.join(', ') }), 'error');
        }
      } finally {
        this.pasteSubmit.disabled = false;
        this.pasteSubmit.textContent = t('quickOrder.addSkus', 'Add SKUs');
        this.setSearchLoading(false);
      }
    }

    async addAllToCart() {
      const items = [...this.items.values()];
      if (!items.length) return;

      const unavailableItems = items.filter((item) => !item.available);
      if (unavailableItems.length) {
        this.setStatus(t('quickOrder.removeUnavailable', 'Remove unavailable products first.'), 'error');
        return;
      }

      this.addAllButton.disabled = true;
      this.addAllLabel.textContent = t('quickOrder.adding', 'Adding…');
      this.setStatus(t('quickOrder.addingProducts', 'Products are being added to the cart.'));

      const payload = {
        items: items.map((item) => ({ id: Number(item.variantId), quantity: item.quantity })),
      };

      try {
        const response = await fetch(`${this.rootUrl}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (!response.ok) {
          const message = data.description || data.message || t('quickOrder.cartAddError', 'The order could not be added to the cart.');
          throw new Error(message);
        }

        this.setStatus(t('quickOrder.allAdded', 'All products have been added to the cart.'), 'success');
        this.addAllLabel.textContent = t('quickOrder.addedToCart', 'Added to cart');
        if (this.cartLink) this.cartLink.hidden = false;
        await this.refreshThemeCart();
      } catch (error) {
        this.setStatus(error.message || t('quickOrder.generalError', 'Something went wrong. Check the quantities and try again.'), 'error');
        this.addAllLabel.textContent = t('quickOrder.addAll', 'Add all to cart');
        this.addAllButton.disabled = false;
      }
    }

    async refreshThemeCart() {
      try {
        if (window.MinimogEvents && window.MinimogTheme && MinimogTheme.pubSubEvents) {
          window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { source: 'md-quick-order' });
        }

        const drawer = document.querySelector('m-cart-drawer');
        if (drawer && typeof drawer.onCartDrawerUpdate === 'function') {
          drawer.onCartDrawerUpdate();
          window.setTimeout(() => {
  const t = (key, fallback = '', replacements = {}) => window.MDT ? window.MDT(key, fallback, replacements) : fallback;
            if (typeof drawer.open === 'function') drawer.open();
          }, 260);
          return;
        }

        const cart = await fetch(`${this.rootUrl}cart.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json());
        document.querySelectorAll('.m-cart-count-bubble').forEach((count) => {
          count.textContent = cart.item_count;
          count.classList.toggle('m:hidden', cart.item_count === 0);
        });
      } catch (error) {
        // De cart-link blijft beschikbaar als terugvaloptie.
      }
    }

    resetCartSuccess() {
      if (this.cartLink) this.cartLink.hidden = true;
      if (this.addAllLabel) this.addAllLabel.textContent = t('quickOrder.addAll', 'Add all to cart');
      this.setStatus('');
      this.addAllButton.disabled = this.items.size === 0 || [...this.items.values()].some((item) => !item.available);
    }

    setPasteStatus(message, type = '') {
      if (!this.pasteStatus) return;
      this.pasteStatus.textContent = message;
      this.pasteStatus.classList.toggle('is-error', type === 'error');
      this.pasteStatus.classList.toggle('is-success', type === 'success');
    }

    setStatus(message, type = '') {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.classList.toggle('is-error', type === 'error');
      this.status.classList.toggle('is-success', type === 'success');
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency }).format((Number(cents) || 0) / 100);
      } catch (error) {
        return `${this.currency} ${((Number(cents) || 0) / 100).toFixed(2)}`;
      }
    }

    toPositiveInt(value, fallback) {
      const parsed = Number.parseInt(value, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    }

    wait(ms) {
      return new Promise((resolve) => window.setTimeout(resolve, ms));
    }

    escapeHtml(value) {
      return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }

    escapeAttr(value) {
      return this.escapeHtml(value);
    }
  }

  const init = () => {
    document.querySelectorAll('[data-md-quick-order]').forEach((root) => {
      if (root.dataset.mdQuickOrderReady === 'true') return;
      root.dataset.mdQuickOrderReady = 'true';
      new MDQuickOrder(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
